import { useIonRouter } from '@ionic/react';
import { ProductsPublicApi } from '@up2stay/up2promo-api-client';
import {
  Button,
  Card,
  CardContent,
  cn,
  healthAttentionFgClass,
  healthAttentionIconTileClass,
  healthCriticalIconTileClass,
  healthInfoFgClass,
  healthOkFgClass,
  healthOkIconSoftClass,
  healthOkSurfaceClass,
} from '@up2stay/up2promo-ui';
import {
  CheckCircle,
  Clock,
  Info,
  Loader2,
  RefreshCw,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { shellPaths } from '@/core/router/app-paths';

import { useIntercom } from '@/core/hooks/use-intercom';
import { readClaimApiError } from '@/core/lib/claim-requests';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { AppIonPage } from '@/core/components/app-ion-page';

/**
 * What the verify call answered (DEV-756): each error code gets its own copy
 * and tone; anything unknown (and a missing token) reads as an invalid link.
 */
type VerifyError =
  | 'claimExpired'
  | 'claimSuperseded'
  | 'alreadyClaimed'
  | 'claimInvalid';
type VerifyStatus = 'loading' | 'success' | VerifyError;

const POST_VERIFY_PATH = shellPaths.moreProducts;
const SETUP_PATH = shellPaths.moreProductsSetup;

const TITLE_CLASS = 'text-[15.5px] font-bold leading-tight text-foreground';
const BODY_CLASS = 'text-[13.5px] leading-relaxed text-muted-foreground';

function toVerifyError(err: unknown): VerifyError {
  const { code } = readClaimApiError(err);
  switch (code) {
    case 'claimExpired':
    case 'claimSuperseded':
    case 'alreadyClaimed':
      return code;
    default:
      return 'claimInvalid';
  }
}

/**
 * A claim token is single-use: StrictMode's double effect (and a remount)
 * must reuse the first call instead of consuming the token twice.
 */
const verifyCalls = new Map<string, Promise<unknown>>();

function verifyOnce(token: string): Promise<unknown> {
  let call = verifyCalls.get(token);
  if (!call) {
    call = ProductsPublicApi.claimVerify({ body: { token } });
    verifyCalls.set(token, call);
  }
  return call;
}

const TONES: Record<
  VerifyError,
  { Icon: LucideIcon; tile: string; title: string }
> = {
  claimExpired: {
    Icon: Clock,
    tile: healthAttentionIconTileClass,
    title: healthAttentionFgClass,
  },
  claimSuperseded: {
    Icon: RefreshCw,
    tile: healthAttentionIconTileClass,
    title: healthAttentionFgClass,
  },
  alreadyClaimed: {
    Icon: Info,
    tile: 'bg-[color:var(--info-bg)] text-[color:var(--info-fg)]',
    title: healthInfoFgClass,
  },
  claimInvalid: {
    Icon: XCircle,
    tile: healthCriticalIconTileClass,
    title: 'text-destructive',
  },
};

export function ClaimVerifyPage() {
  const router = useIonRouter();
  const location = useLocation();
  const dict = useDictionary();
  const t = dict.claim.verify;
  const intercom = useIntercom();
  const token = new URLSearchParams(location.search).get('token')?.trim() ?? '';
  const [status, setStatus] = useState<VerifyStatus>(() =>
    token ? 'loading' : 'claimInvalid',
  );

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    verifyOnce(token)
      .then(() => {
        if (!cancelled) setStatus('success');
      })
      .catch((err: unknown) => {
        if (!cancelled) setStatus(toVerifyError(err));
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const goTo = (path: string) => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('authReturnUrl', path);
    }
    router.push(path, 'root', 'replace');
  };

  const copy: Record<VerifyError, { title: string; message: string }> = {
    claimExpired: { title: t.expiredTitle, message: t.expiredMessage },
    claimSuperseded: {
      title: t.supersededTitle,
      message: t.supersededMessage,
    },
    alreadyClaimed: {
      title: t.alreadyClaimedTitle,
      message: t.alreadyClaimedMessage,
    },
    claimInvalid: { title: t.invalidTitle, message: t.invalidMessage },
  };

  const actions: Record<VerifyError, ReactNode> = {
    claimExpired: (
      <Button type="button" variant="primary" onClick={() => goTo(SETUP_PATH)}>
        {t.requestNewLink}
      </Button>
    ),
    claimSuperseded: (
      <div className="flex flex-wrap justify-center gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => goTo(SETUP_PATH)}
        >
          {t.checkAgain}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => void intercom.openSupport()}
        >
          {t.support}
        </Button>
      </div>
    ),
    alreadyClaimed: (
      <Button
        type="button"
        variant="outline"
        onClick={() => goTo(POST_VERIFY_PATH)}
      >
        {t.goToProducts}
      </Button>
    ),
    claimInvalid: (
      <Button
        type="button"
        variant="outline"
        onClick={() => goTo(POST_VERIFY_PATH)}
      >
        {t.goToApp}
      </Button>
    ),
  };

  const errorTone =
    status !== 'loading' && status !== 'success' ? TONES[status] : null;

  return (
    <AppIonPage>
      <div className="flex min-h-full flex-col items-center justify-center bg-background p-6">
        <div className="w-full max-w-md" data-claim-verify-status={status}>
          {status === 'loading' && (
            <Card>
              <CardContent className="flex flex-col items-center gap-4 p-5 sm:p-6">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className={BODY_CLASS} role="status">
                  {t.verifying}
                </p>
              </CardContent>
            </Card>
          )}

          {status === 'success' && (
            <Card className={cn(healthOkSurfaceClass)}>
              <CardContent className="flex flex-col items-center gap-4 p-5 sm:p-6">
                <div
                  className={cn(
                    'flex h-12 w-12 items-center justify-center rounded-2xl',
                    healthOkIconSoftClass,
                  )}
                >
                  <CheckCircle className="h-6 w-6" aria-hidden />
                </div>
                <div className="flex flex-col gap-1 text-center">
                  <h1 className={cn(TITLE_CLASS, healthOkFgClass)}>
                    {t.successTitle}
                  </h1>
                  <p className={BODY_CLASS} role="status">
                    {t.successMessage}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => goTo(POST_VERIFY_PATH)}
                >
                  {t.goToProducts}
                </Button>
              </CardContent>
            </Card>
          )}

          {status !== 'loading' && status !== 'success' && errorTone && (
            <Card>
              <CardContent className="flex flex-col items-center gap-4 p-5 sm:p-6">
                <div
                  className={cn(
                    'flex h-12 w-12 items-center justify-center rounded-2xl',
                    errorTone.tile,
                  )}
                >
                  <errorTone.Icon className="h-6 w-6" aria-hidden />
                </div>
                <div className="flex flex-col gap-1 text-center">
                  <h1 className={cn(TITLE_CLASS, errorTone.title)}>
                    {copy[status].title}
                  </h1>
                  <p className={BODY_CLASS} role="alert">
                    {copy[status].message}
                  </p>
                </div>
                {actions[status]}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AppIonPage>
  );
}
